import { $systems, createStore, ignite, listen, reactive } from "kho";
import type { Camera, Object3D, Scene, WebGLRenderer } from "three";
import { $ifcFactory, $objectScale } from "./data/ifc";
import {
  $deleteRequest,
  $editorMode,
  $gizmoInfo,
  $instances,
  $instancesGroup,
  $placeRequest,
  $selection,
  $transformRequest,
  Instance,
  Quat,
  Vec3,
} from "./data/model";
import { $frame, $scene } from "./data/scene";
import { $surfacePick, SurfaceHit } from "./data/surface";
import { $viewer, ViewerHost } from "./data/viewer";
import { ifcObjectFactory } from "./factory/ifcFactory";
import { gizmoSystem } from "./system/gizmoSystem";
import { instancesSystem } from "./system/instances";
import { selectionSystem } from "./system/selectionSystem";
import { surfaceSystem } from "./system/surfaceSystem";
import { viewerAdapterSystem } from "./system/viewerAdapterSystem";

/**
 * Data-driven object store — the POC editor model, ported. instancesSystem is
 * the sole writer of $instances and renders them into the design scene (IFC via
 * ifcObjectFactory). The gizmo / placement only EMIT requests; nothing mutates
 * meshes directly. This runs in its own kho store so it can be brought up
 * alongside the current imperative path and swapped in incrementally.
 */
export type InstanceStore = ReturnType<typeof createInstanceStore>;
export type TransformPatch = {
  id: string;
  position?: Vec3;
  quaternion?: Quat;
  up?: Vec3;
  baseYaw?: number;
  height?: number;
  scale?: number;
};

export function createInstanceStore(scene: Scene, host?: ViewerHost) {
  const store = createStore("editor-instances");
  const { atoms, sets, maps } = reactive(store);
  const listener = listen(store);

  atoms.set($ifcFactory, ifcObjectFactory);
  // viewerAdapterSystem first: it feeds $camera/$renderer/$cloudScenes off
  // $viewer at the start of every $frame, before the gizmo/instance systems read
  // them in their own $frame handlers.
  sets.add($systems, viewerAdapterSystem);
  // surfaceSystem after the adapter (it needs $renderer/$camera/$cloudScenes) and
  // before the gizmo (which reads $surfaceSampler): owns the GPU surface sampler.
  sets.add($systems, surfaceSystem);
  sets.add($systems, instancesSystem);
  sets.add($systems, gizmoSystem); // POC gizmo — shares $selection/$instances/$transformRequest
  sets.add($systems, selectionSystem); // click-to-select → writes $selection
  const dispose = ignite(store);
  atoms.set($scene, scene); // after ignite so the scene effect fires
  if (host) atoms.set($viewer, host);

  let last = 0;
  const tickFrame = () => {
    const now =
      typeof performance !== "undefined" ? performance.now() : last + 16;
    const dt = last ? (now - last) / 1000 : 0;
    last = now;
    listener.emit($frame, { dt, time: now / 1000 });
  };
  return {
    /** Tick $frame — the gizmo runs all its drag math in the $frame loop. */
    frame: tickFrame,
    /**
     * Render the design scene, driven by the Viewer's render loop. Ticks $frame
     * (viewerAdapterSystem republishes camera/renderer/point-cloud; the gizmo
     * runs its drag math), applies the host's adaptive near/far, then draws
     * $scene. This is the design-scene render that used to live in DesignManager.
     */
    renderDesign(renderer: WebGLRenderer, camera: Camera) {
      const scene = atoms.get($scene);
      if (!scene) return;
      tickFrame();
      const host = atoms.get($viewer);
      host?.setDragActive(atoms.get($editorMode) === "drag");
      const cam = camera as any;
      const clip = host ? host.getDesignClip(camera) : null;
      let savedNear: number | undefined;
      let savedFar: number | undefined;
      if (clip && cam.near !== undefined) {
        savedNear = cam.near;
        savedFar = cam.far;
        cam.near = clip.near;
        cam.far = clip.far;
        cam.updateProjectionMatrix();
      }
      renderer.clearDepth();
      renderer.render(scene, camera);
      if (savedNear !== undefined) {
        cam.near = savedNear;
        cam.far = savedFar;
        cam.updateProjectionMatrix();
      }
    },
    /** True while a gizmo handle is being dragged (locks camera orbit). */
    isDragging(): boolean {
      return atoms.get($editorMode) === "drag";
    },
    getGizmoInfo() {
      return atoms.get($gizmoInfo);
    },
    /**
     * Point-cloud surface pick under an NDC coord — the drag&drop placement
     * probe. surfaceSystem publishes the implementation in $surfacePick (coarse
     * CPU raycast + optional GPU PCA refine); this reads it. Null until a
     * renderer/camera/cloud is available or when nothing is hit.
     */
    surfacePick(ndcX: number, ndcY: number, refine: boolean): SurfaceHit | null {
      const fn = atoms.get($surfacePick);
      return fn ? fn(ndcX, ndcY, refine) : null;
    },
    /** Create a fresh instance from an IFC url at a surface pivot. */
    place(
      url: string,
      position: Vec3,
      quaternion: Quat,
      up: Vec3,
      committedId?: string | null,
    ) {
      listener.emit($placeRequest, { url, position, quaternion, up, committedId });
    },
    /** Merge a partial transform into an instance (position/quat/up/yaw/height). */
    transform(patch: TransformPatch) {
      listener.emit($transformRequest, patch);
    },
    remove(id: string) {
      listener.emit($deleteRequest, { id });
    },
    /** Attach a DB committedId to an instance after it is persisted (POST). */
    setCommittedId(instanceId: string, committedId: string) {
      const inst = maps.get($instances, instanceId);
      if (inst) maps.set($instances, instanceId, { ...inst, committedId });
    },
    setObjectScale(n: number) {
      atoms.set($objectScale, n);
    },
    getInstances(): Instance[] {
      return [...atoms.get($instances).values()];
    },
    /** The currently-selected instance (to read its live transform). */
    getSelected(): Instance | null {
      const id = atoms.get($selection);
      return id ? maps.get($instances, id) ?? null : null;
    },
    /** Find an instance by its persisted committedId (DB id), or null. */
    getByCommittedId(committedId: string): Instance | null {
      for (const inst of atoms.get($instances).values())
        if (inst.committedId === committedId) return inst;
      return null;
    },
    getSelection(): string | null {
      return atoms.get($selection);
    },
    setSelection(id: string | null) {
      atoms.set($selection, id);
    },
    getGroup(): Object3D | null {
      return atoms.get($instancesGroup);
    },
    dispose,
  };
}
